/**
 * Direct browser → Fulcrum (ElectrumX) client over WebSocket Secure (WSS).
 *
 * WHY THIS EXISTS
 * ---------------
 * Public BCH *web* explorers (Blockchair, chaingraph demo, bchexplorer, the
 * fullstack.cash REST host, …) are hostile to server-side polling: they
 * rate-limit, bot-block (HTTP 403/430), go down (520), or serve HTML instead
 * of JSON. Chaining four of them behind a Netlify proxy just means they all
 * fail at once — which is exactly what happened ("backend: none").
 *
 * Fulcrum / ElectrumX is the indexer that real BCH wallets use to watch
 * addresses. It speaks the Electrum Cash JSON-RPC protocol, is purpose-built
 * for "has this address received anything?", does NOT bot-block, and — crucially
 * — exposes a **WSS** port (50004) that a browser can connect to directly.
 *
 * So we drop the whole proxy and talk to a pool of public Fulcrum servers from
 * the browser, with automatic failover between them.
 *
 * Protocol reference: https://electrum-cash-protocol.readthedocs.io/
 * Methods used:
 *   - blockchain.scripthash.listunspent  → current UTXOs (incl. mempool/0-conf)
 *   - blockchain.scripthash.get_history  → confirmed + mempool tx history
 *
 * Electrum indexes by "scripthash" = SHA-256 of the output locking script,
 * byte-reversed and hex-encoded. We derive it from a CashAddr locally.
 */

import { sha256 } from '@noble/hashes/sha2';

// ── Public BCH Fulcrum servers with a browser-reachable WSS port ──────────────
// Sourced from the mainnet.cash / mainnet-js default server list. Order matters:
// we try them in sequence and stick with the first that connects.
export const FULCRUM_SERVERS = [
  'wss://blackie.c3-soft.com:50004',
  'wss://bch.imaginary.cash:50004',
  'wss://electrum.imaginary.cash:50004',
  'wss://fulcrum.fountainhead.cash:50004',
  'wss://cashnode.bch.ninja:50004',
];

const RPC_TIMEOUT_MS = 10_000;
const CONNECT_TIMEOUT_MS = 6_000;

// ── CashAddr → locking bytecode → scripthash ─────────────────────────────────
const CASHADDR_CHARSET = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';

function polymod(values: number[]): bigint {
  const GEN = [0x98f2bc8e61n, 0x79b76d99e2n, 0xf33e5fb3c4n, 0xae2eabe2a8n, 0x1e4f43e470n];
  let chk = 1n;
  for (const v of values) {
    const top = chk >> 35n;
    chk = ((chk & 0x07ffffffffn) << 5n) ^ BigInt(v);
    for (let i = 0; i < 5; i++) {
      if ((top >> BigInt(i)) & 1n) chk ^= GEN[i];
    }
  }
  return chk ^ 1n;
}

function prefixToWords(prefix: string): number[] {
  const words: number[] = [];
  for (let i = 0; i < prefix.length; i++) words.push(prefix.charCodeAt(i) & 0x1f);
  return words;
}

function convertBits(data: number[], from: number, to: number, pad: boolean): number[] | null {
  let acc = 0;
  let bits = 0;
  const ret: number[] = [];
  const maxv = (1 << to) - 1;
  for (const value of data) {
    if (value < 0 || value >> from !== 0) return null;
    acc = (acc << from) | value;
    bits += from;
    while (bits >= to) {
      bits -= to;
      ret.push((acc >> bits) & maxv);
    }
  }
  if (pad) {
    if (bits > 0) ret.push((acc << (to - bits)) & maxv);
  } else if (bits >= from || ((acc << (to - bits)) & maxv)) {
    return null;
  }
  return ret;
}

interface DecodedAddr {
  type: number; // 0 = P2PKH, 1 = P2SH
  hash: Uint8Array;
}

function decodeCashAddr(bare: string): DecodedAddr | null {
  const lower = bare.toLowerCase();
  const prefix = 'bitcoincash';
  const data: number[] = [];
  for (const ch of lower) {
    const idx = CASHADDR_CHARSET.indexOf(ch);
    if (idx === -1) return null;
    data.push(idx);
  }
  const checksumInput = [...prefixToWords(prefix), 0, ...data];
  if (polymod(checksumInput) !== 0n) return null;

  const payload = data.slice(0, -8);
  const bytes = convertBits(payload, 5, 8, false);
  if (!bytes || bytes.length === 0) return null;

  const versionByte = bytes[0];
  const type = (versionByte >> 3) & 0x1f;
  const hash = Uint8Array.from(bytes.slice(1));
  return { type, hash };
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.substr(i * 2, 2), 16);
  return out;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Build the P2PKH/P2SH locking bytecode for a cashaddr. Returns null if
 * unsupported.
 *
 * Token-awareness does NOT change the locking script: a Token-Aware P2PKH
 * (type 2) produces the exact same script as a plain P2PKH (type 0), and
 * Token-Aware P2SH (type 3) the same as P2SH (type 1). The version byte only
 * signals wallet capability off-chain. So we map 0/2 → P2PKH and 1/3 → P2SH,
 * which means `z…` and `q…` forms of the same key yield the same scripthash.
 */
function addrToLockingBytecode(bare: string): Uint8Array | null {
  const decoded = decodeCashAddr(bare);
  if (!decoded) return null;
  const h = bytesToHex(decoded.hash);
  let hex: string;
  if (decoded.type === 0 || decoded.type === 2) hex = `76a914${h}88ac`; // (Token-Aware) P2PKH
  else if (decoded.type === 1 || decoded.type === 3) hex = `a914${h}87`; // (Token-Aware) P2SH
  else return null;
  return hexToBytes(hex);
}

/**
 * Electrum scripthash: SHA-256 of the locking script, byte-reversed, hex-encoded.
 * Throws on an address we can't decode.
 */
export function addressToScripthash(address: string): string {
  const bare = address.replace(/^bitcoincash:/i, '').trim();
  const script = addrToLockingBytecode(bare);
  if (!script) throw new Error('unsupported or invalid BCH address');
  const hash = sha256(script);
  hash.reverse(); // Electrum uses little-endian scripthash
  return bytesToHex(hash);
}

// ── CashAddr ENCODER + token-aware conversion ────────────────────────────────
// CashAddr version byte layout (CashTokens CHIP):
//   bit 7      : reserved (0)
//   bits 3..6  : type   (0=P2PKH, 1=P2SH, 2=Token-Aware P2PKH, 3=Token-Aware P2SH)
//   bits 0..2  : size   (0 = 20-byte hash160)
// So the version byte is simply (type << 3). Token-aware P2PKH = 16, P2SH = 24.

/** Encode a (type, hash) pair into a full `bitcoincash:` CashAddr string. */
function encodeCashAddr(type: number, hash: Uint8Array, prefix = 'bitcoincash'): string {
  // size bits: only 20-byte hash160 is used here (size code 0).
  const sizeCode = 0; // 20 bytes
  const versionByte = (type << 3) | sizeCode;
  const payloadBytes = [versionByte, ...Array.from(hash)];
  const payload5 = convertBits(payloadBytes, 8, 5, true);
  if (!payload5) throw new Error('cashaddr payload conversion failed');

  const checksumInput = [...prefixToWords(prefix), 0, ...payload5, 0, 0, 0, 0, 0, 0, 0, 0];
  const mod = polymod(checksumInput);
  const checksum: number[] = [];
  for (let i = 0; i < 8; i++) {
    checksum.push(Number((mod >> BigInt(5 * (7 - i))) & 0x1fn));
  }

  const combined = [...payload5, ...checksum];
  const body = combined.map((v) => CASHADDR_CHARSET[v]).join('');
  return `${prefix}:${body}`;
}

/**
 * Convert any BCH CashAddr to its TOKEN-AWARE equivalent (same key/hash, new
 * version byte): P2PKH (q…) → Token-Aware P2PKH (z…); P2SH (p…) → Token-Aware
 * P2SH (r…). If the address is already token-aware, it's returned normalized.
 * Throws on an address we can't decode.
 */
export function toTokenAwareAddress(address: string): string {
  const bare = address.replace(/^bitcoincash:/i, '').trim();
  const decoded = decodeCashAddr(bare);
  if (!decoded) throw new Error('unsupported or invalid BCH address');

  // Map base type → token-aware type.
  let tokenType: number;
  if (decoded.type === 0 || decoded.type === 2) tokenType = 2; // P2PKH → token P2PKH
  else if (decoded.type === 1 || decoded.type === 3) tokenType = 3; // P2SH → token P2SH
  else throw new Error('unsupported address type for tokens');

  return encodeCashAddr(tokenType, decoded.hash);
}

/** True if the address is already a token-aware CashAddr (type 2 or 3). */
export function isTokenAwareAddress(address: string): boolean {
  const bare = address.replace(/^bitcoincash:/i, '').trim();
  const decoded = decodeCashAddr(bare);
  return !!decoded && (decoded.type === 2 || decoded.type === 3);
}

// ── Electrum JSON-RPC over WebSocket ──────────────────────────────────────────

interface RpcResponse {
  id?: number;
  result?: unknown;
  error?: { code?: number; message?: string } | string;
}

/** CashToken data attached to a UTXO (Electrum protocol 1.5.0+). */
export interface FulcrumTokenData {
  /** Fungible amount as a DECIMAL STRING (can exceed 2^53 — must use BigInt). */
  amount: string;
  /** 32-byte category id, big-endian (reversed) hex — matches display order. */
  category: string;
  nft?: {
    capability: 'none' | 'mutable' | 'minting';
    commitment: string; // hex
  };
}

export interface FulcrumUtxo {
  tx_hash: string;
  tx_pos: number;
  height: number;
  value: number; // satoshis (dust carrying the token, if token_data present)
  token_data?: FulcrumTokenData;
}

export type TokenFilter = 'include_tokens' | 'exclude_tokens' | 'tokens_only';

export interface FulcrumHistoryItem {
  tx_hash: string;
  height: number; // 0 = mempool/unconfirmed, >0 = confirmed block height
}

/**
 * A single live WSS connection to one Fulcrum server. Handles request/response
 * correlation by id. Not pooled — we create one, use it, keep it for the life
 * of the module (reconnecting on failure).
 */
class FulcrumConnection {
  private ws: WebSocket | null = null;
  private url: string;
  private nextId = 1;
  private pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> }>();
  private connecting: Promise<void> | null = null;
  private buffer = '';

  constructor(url: string) {
    this.url = url;
  }

  get serverUrl(): string {
    return this.url;
  }

  private async connect(): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;
    if (this.connecting) return this.connecting;

    this.connecting = new Promise<void>((resolve, reject) => {
      let settled = false;
      const ws = new WebSocket(this.url);
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        try { ws.close(); } catch { /* ignore */ }
        reject(new Error(`connect timeout: ${this.url}`));
      }, CONNECT_TIMEOUT_MS);

      ws.onopen = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.ws = ws;
        resolve();
      };
      ws.onerror = () => {
        if (settled) {
          this.failAll(new Error('websocket error'));
          return;
        }
        settled = true;
        clearTimeout(timer);
        reject(new Error(`connect failed: ${this.url}`));
      };
      ws.onclose = () => {
        this.ws = null;
        this.failAll(new Error('websocket closed'));
      };
      ws.onmessage = (ev) => this.onMessage(ev.data);
    }).finally(() => {
      this.connecting = null;
    });

    return this.connecting;
  }

  private onMessage(data: unknown): void {
    // Electrum sends newline-delimited JSON objects.
    this.buffer += typeof data === 'string' ? data : '';
    let idx: number;
    while ((idx = this.buffer.indexOf('\n')) >= 0) {
      const line = this.buffer.slice(0, idx).trim();
      this.buffer = this.buffer.slice(idx + 1);
      if (!line) continue;
      this.handleLine(line);
    }
    // Some servers don't newline-terminate the final frame; try to parse the
    // whole buffer as a single object if it looks complete.
    if (this.buffer.trim().startsWith('{') && this.buffer.trim().endsWith('}')) {
      const whole = this.buffer.trim();
      this.buffer = '';
      this.handleLine(whole);
    }
  }

  private handleLine(line: string): void {
    let msg: RpcResponse;
    try {
      msg = JSON.parse(line) as RpcResponse;
    } catch {
      return; // ignore unparseable frames
    }
    if (typeof msg.id !== 'number') return; // subscription notification — ignore
    const entry = this.pending.get(msg.id);
    if (!entry) return;
    this.pending.delete(msg.id);
    clearTimeout(entry.timer);
    if (msg.error) {
      const em = typeof msg.error === 'string' ? msg.error : msg.error.message || 'rpc error';
      entry.reject(new Error(em));
    } else {
      entry.resolve(msg.result);
    }
  }

  private failAll(err: Error): void {
    for (const [, entry] of this.pending) {
      clearTimeout(entry.timer);
      entry.reject(err);
    }
    this.pending.clear();
  }

  async call(method: string, params: unknown[] = []): Promise<unknown> {
    await this.connect();
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) throw new Error('not connected');

    const id = this.nextId++;
    const payload = JSON.stringify({ id, method, params }) + '\n';

    return new Promise<unknown>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`rpc timeout: ${method}`));
      }, RPC_TIMEOUT_MS);
      this.pending.set(id, { resolve, reject, timer });
      try {
        ws.send(payload);
      } catch (e) {
        this.pending.delete(id);
        clearTimeout(timer);
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }

  close(): void {
    try { this.ws?.close(); } catch { /* ignore */ }
    this.ws = null;
  }
}

// ── Pool with failover ────────────────────────────────────────────────────────
// We keep a single "active" connection and only rotate to the next server when
// the current one errors. A tiny handshake (server.version) verifies liveness.

let active: FulcrumConnection | null = null;
let activeIndex = 0;

async function getConnection(): Promise<FulcrumConnection> {
  if (active) return active;

  let lastErr: Error | null = null;
  for (let i = 0; i < FULCRUM_SERVERS.length; i++) {
    const idx = (activeIndex + i) % FULCRUM_SERVERS.length;
    const url = FULCRUM_SERVERS[idx];
    const conn = new FulcrumConnection(url);
    try {
      // Handshake — proves the socket is a live Electrum server, not just open.
      // Negotiate protocol 1.5.1 so CashToken `token_data` is returned by
      // listunspent (required for stablecoin / PUSD detection).
      await conn.call('server.version', ['bchpay.cash', '1.5.1']);
      active = conn;
      activeIndex = idx;
      return conn;
    } catch (e) {
      conn.close();
      lastErr = e instanceof Error ? e : new Error(String(e));
    }
  }
  throw lastErr ?? new Error('no Fulcrum servers reachable');
}

/** Drop the active connection so the next call re-selects a server. */
function rotate(): void {
  if (active) {
    active.close();
    active = null;
  }
  activeIndex = (activeIndex + 1) % FULCRUM_SERVERS.length;
}

/**
 * Run an Electrum RPC with automatic failover: if the active server fails, we
 * rotate to the next one and retry, up to the size of the pool.
 */
async function withFailover<T>(fn: (conn: FulcrumConnection) => Promise<T>): Promise<{ result: T; server: string }> {
  let lastErr: Error | null = null;
  for (let attempt = 0; attempt < FULCRUM_SERVERS.length; attempt++) {
    let conn: FulcrumConnection;
    try {
      conn = await getConnection();
    } catch (e) {
      lastErr = e instanceof Error ? e : new Error(String(e));
      break;
    }
    try {
      const result = await fn(conn);
      return { result, server: conn.serverUrl };
    } catch (e) {
      lastErr = e instanceof Error ? e : new Error(String(e));
      rotate(); // this server misbehaved — try the next on the next loop
    }
  }
  throw lastErr ?? new Error('all Fulcrum servers failed');
}

/** List unspent outputs (incl. mempool/0-conf) for an address. */
export async function fulcrumListUnspent(
  address: string
): Promise<{ utxos: FulcrumUtxo[]; server: string }> {
  const scripthash = addressToScripthash(address);
  const { result, server } = await withFailover(async (conn) => {
    const r = await conn.call('blockchain.scripthash.listunspent', [scripthash]);
    return (Array.isArray(r) ? r : []) as FulcrumUtxo[];
  });
  return { utxos: result, server };
}

/** Get confirmed + mempool transaction history for an address. */
export async function fulcrumGetHistory(
  address: string
): Promise<{ history: FulcrumHistoryItem[]; server: string }> {
  const scripthash = addressToScripthash(address);
  const { result, server } = await withFailover(async (conn) => {
    const r = await conn.call('blockchain.scripthash.get_history', [scripthash]);
    return (Array.isArray(r) ? r : []) as FulcrumHistoryItem[];
  });
  return { history: result, server };
}

/**
 * List unspent outputs for an address, filtered by token presence.
 *
 * `token_filter`:
 *   - 'tokens_only'    → only UTXOs carrying a CashToken (what we want for PUSD)
 *   - 'include_tokens' → both plain and token UTXOs
 *   - 'exclude_tokens' → plain BCH only
 *
 * Note: the scripthash is computed from the *locking script*, which is identical
 * for a normal (q…) and token-aware (z…) address of the same key — so you can
 * pass either form here and get the same results.
 */
export async function fulcrumListUnspentTokens(
  address: string,
  tokenFilter: TokenFilter = 'tokens_only'
): Promise<{ utxos: FulcrumUtxo[]; server: string }> {
  const scripthash = addressToScripthash(address);
  const { result, server } = await withFailover(async (conn) => {
    const r = await conn.call('blockchain.scripthash.listunspent', [scripthash, tokenFilter]);
    return (Array.isArray(r) ? r : []) as FulcrumUtxo[];
  });
  return { utxos: result, server };
}
